import { useBrokersServiceBrokersBrokersList } from '@insurance/api/queries'
import { BrokerList } from '@insurance/api/requests'
import { SearchInput, SkeletonWrapper, TitleSection } from '@shared/components'
import { usePaginationConfig, useQueryUpdate, useSorting } from '@shared/hooks'

import { Button, Flex, Modal } from 'antd'
import { Add } from 'iconsax-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ChangeAdminModal,
  CompaniesManagementFilters,
  CompaniesManagementForm,
  CompaniesManagementTable,
  CompaniesProductsForm
} from '../components'

const FORM_NAME = 'companies-management-form'
const FIRST_STEP = 0
const SECOND_STEP = 1

type Props = {
  onNameClick?: (broker: BrokerList) => void
}

export const CompaniesManagementPage = ({ onNameClick }: Props) => {
  const { t } = useTranslation()
  const { params, updateQuery } = useQueryUpdate()
  const { triggerSort, getSortedTableColumns } = useSorting<BrokerList>()
  const [isModalBtnLoading, setIsModalBtnLoading] = useState(false)
  const [isModalLoading, setIsModalLoading] = useState(false)

  const [formStep, setFormStep] = useState(0)
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [editId, setEditId] = useState<number>()
  const [changeAdminId, setChangeAdminId] = useState<number>()

  const { data, isLoading } = useBrokersServiceBrokersBrokersList(params)

  const onOpenModal = () => setIsOpenModal(true)
  const onCloseModal = () => {
    setEditId(undefined)
    setIsOpenModal(false)
    setFormStep(FIRST_STEP)
  }

  const onEdit = (id: number) => {
    setEditId(id)
    onOpenModal()
    setFormStep(FIRST_STEP)
  }

  const onChangeAdmin = (id: number) => setChangeAdminId(id)
  const onCloseChangeAdmin = () => setChangeAdminId(undefined)

  const onNextStep = (id: number) => {
    setEditId(id)
    setFormStep(SECOND_STEP)
  }

  const onPreviousStep = () => {
    setFormStep((prev) => prev - 1)
  }

  const companiesSteps = useMemo(
    () => [
      <CompaniesManagementForm
        companyId={editId}
        name={FORM_NAME}
        onNextStep={onNextStep}
        onLoading={setIsModalBtnLoading}
        onLoadingModal={setIsModalLoading}
        onAddCompanyId={setEditId}
      />,
      <CompaniesProductsForm
        companyId={editId}
        name={FORM_NAME}
        onNextStep={onCloseModal}
      />
    ],
    [editId]
  )

  const cancelButtonText =
    formStep === FIRST_STEP ? t('cancel') : t('previous_step')
  const okButtonText = formStep < SECOND_STEP ? t('next_step') : t('save')

  const onClickCancel = () => {
    if (formStep === FIRST_STEP) {
      onCloseModal()
    } else {
      onPreviousStep()
    }
  }

  return (
    <>
      <TitleSection
        title={t('companies_management')}
        count={data?.count}
        isLoading={isLoading}
      >
        <SearchInput placeholder={t('search')} />
      </TitleSection>

      <Flex vertical gap={16}>
        <Flex justify="space-between">
          <CompaniesManagementFilters />

          <Button icon={<Add />} type="primary" onClick={onOpenModal}>
            {t('add_company')}
          </Button>
        </Flex>

        <CompaniesManagementTable
          data={data?.results}
          loading={isLoading}
          triggerSort={triggerSort}
          onNameClick={onNameClick}
          getSortedTableColumns={getSortedTableColumns}
          onEdit={onEdit}
          onChangeAdmin={onChangeAdmin}
          pagination={{
            ...usePaginationConfig(data),
            onChange: (page: number, pageSize: number) =>
              updateQuery({
                ...params,
                page: page.toString(),
                pageSize: pageSize.toString()
              })
          }}
        />
      </Flex>

      {changeAdminId && (
        <ChangeAdminModal
          brokerId={changeAdminId}
          open={!!changeAdminId}
          onClose={onCloseChangeAdmin}
        />
      )}

      <Modal
        width={800}
        title={t(editId ? 'edit_company' : 'add_company')}
        open={isOpenModal}
        okText={okButtonText}
        cancelText={cancelButtonText}
        destroyOnHidden
        cancelButtonProps={{
          onClick: onClickCancel
        }}
        okButtonProps={{
          form: FORM_NAME,
          htmlType: 'submit',
          loading: isModalBtnLoading
        }}
        loading={isModalLoading}
        onCancel={onCloseModal}
      >
        <SkeletonWrapper paragraph={{ rows: 10 }} loading={isLoading}>
          {companiesSteps[formStep]}
        </SkeletonWrapper>
      </Modal>
    </>
  )
}
